// NavHeader.jsx
import React, { useMemo, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { matchPath, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import MeAvatar from '../container/MeAvatar.js';
import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import Tooltip from '../component/Tooltip.jsx';
import HomeSearchBar from '../container/HomeSearchBar.js';
import getUserProfileUrl from '../resource/getUserProfileUrl.js';
import UserLevelBadge from '../container/UserLevelBadge.js';
import LoginSignUpButton from '../container/LoginSignUpButton.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';

import {
  headerHeight,
  color,
  textColor,
  breakpoint,
} from '../style/variables.js';

import { ButtonId } from '../resource/trackingConstants.js';
import { SHORT_PATHS } from '../resource/getShouldShowFooter.js';
import getIsMatchedPathname from '../resource/getIsMatchedPathname.js';
import { removeLangFromPath } from '../resource/langPath.js';
import { RANGE_24H } from '../resource/leaderboardConstants.js';
import getNavigationIcon, {
  NavigationId,
} from '../resource/getNavigationIcon.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import defaults from '../resource/defaults.js';

import NewBadgeIconSource from '../../img/img_badge_new.svg';
import CreatorIconSource from '../../img/ic_badge_creator.svg';
import BackIconSource from '../../img/ic_topbar_back.svg';
import ShopIconSource from '../../img/ic_diamond_shop.svg';
import MoreIconSource from '../../img/ic_topbar_more.svg';
import InfoIconSource from '../../img/ic_info_20.svg';
import MediaLibraryIconSource from '../../img/ic_media_library.svg';
import logo from '../../img/login_swag_icon.svg';

import useNavHeader from '../hook/useNavHeader.js';

import {
  Icon,
  IconSection,
  SearchWrapper,
  TooltipStyle,
} from '../style/NavHeader.js';

const AVATAR_SIZE = 32;
const AVATAR_BORDER_SIZE = 1;
const TAB_CONFIG = {
  explore: {
    id: NavigationId.BROWSE_EXPLORE,
    linkTo: '/',
    pathArray: ['/'],
    buttonId: ButtonId.Tab.TabExplore,
    buttonNameI18nKey: 'explore_title',
  },
  following: {
    id: NavigationId.BROWSE_FOLLOWING,
    linkTo: '/following',
    pathArray: ['/following'],
    buttonId: ButtonId.Tab.TabFollowing,
    buttonNameI18nKey: 'title_following_list',
  },
  'category-overview': {
    id: NavigationId.CATEGORY,
    linkTo: '/category-overview',
    pathArray: ['/category-overview'],
    buttonId: ButtonId.Tab.TabCategory,
    buttonNameI18nKey: 'general_categories',
  },
  livestream: {
    id: NavigationId.LIVESTREAM,
    linkTo: '/livestream',
    pathArray: ['/livestream', '/livestream/:index'],
    buttonId: ButtonId.Tab.TabButtonLivestream,
    buttonNameI18nKey: 'tabbar_livestream',
  },
  video: {
    id: NavigationId.VIDEO,
    linkTo: '/video',
    pathArray: ['/video'],
    buttonId: ButtonId.Tab.TabButtonDiscover,
    buttonNameI18nKey: 'tabbar_discover',
  },
  shorts: {
    id: NavigationId.SHORTS,
    linkTo: '/shorts',
    pathArray: SHORT_PATHS,
    buttonId: ButtonId.Tab.TabButtonShorts,
    buttonNameI18nKey: 'shorts',
  },
};
const authRequiredTabs = ['following'];

const componentZIndex = {
  header: 8, // should higher than post action buttons
};
const navigationList = [
  {
    id: NavigationId.CHAT,
    pathArray: ['/chat/*'],
    isNeedAuth: false,
    buttonId: ButtonId.Tab.TabButtonChat,
    buttonNameI18nKey: 'tabbar_chat',
    getPath: () => '/chat',
    getIconSource: ({ isMatch, hasUnread } = {}) =>
      getNavigationIcon({
        navigationId: NavigationId.CHAT,
        isMatch,
        hasUnread,
      }),
  },
  {
    id: NavigationId.LEADERBOARD,
    pathArray: ['/leaderboard/:region/:category/:range'],
    isNeedAuth: false,
    buttonId: ButtonId.Tab.TabButtonLeaderboard,
    buttonNameI18nKey: 'leaderboard',
    getIconSource: ({ isMatch } = {}) =>
      getNavigationIcon({ navigationId: NavigationId.LEADERBOARD, isMatch }),
    getPath: ({ region, category } = {}) =>
      `/leaderboard/${region}/${category}/${RANGE_24H}`,
  },
];

export const NavHeader = ({
  clipCategory = null,
  meUsername = '',
  deviceWidth = 768, // must more than minWidth mobile = 768
  latestFeedRenewUnix = null,
  hasUnread = false,
  isAuthed = false,
  isDiamondShopOpen = false,
  isHamburgerMenuOpen = false,
  isShortEnabled = false,
  feedNameWithQuery = null,
  renewFeedId = null,
  brandname = 'SWAG',
  shouldShowPasswordBindingHint = false,
  leaderboardRegions = defaults.EMPTY_ARRAY,
  leaderboardCategories = defaults.EMPTY_ARRAY,
  headerTabs = defaults.EMPTY_ARRAY,
  goBack = defaults.NULL_FUNCTION,
  toggleModal = defaults.NULL_FUNCTION,
  addvalue = defaults.NULL_FUNCTION,
  fetchFeeds = defaults.NULL_FUNCTION,
  subscribeFeedChannel = defaults.NULL_FUNCTION,
  unsubscribeFeedChannel = defaults.NULL_FUNCTION,
}) => {
  const leaderboardComponentIndex = navigationList.findIndex(
    ({ id }) => NavigationId.LEADERBOARD === id
  );
  const { pathname } = useLocation();
  const cleanPathname = removeLangFromPath(pathname);
  const { t } = useTranslation();
  const clipCategoryRef = useRef(clipCategory);

  // Subscribe/Unsubscribe clip feed channel
  useEffect(() => {
    if (clipCategory) {
      clipCategoryRef.current = clipCategory;
      subscribeFeedChannel({ feedName: clipCategory });
    }
    return () => {
      unsubscribeFeedChannel({ feedName: clipCategoryRef.current });
    };
  }, [clipCategory, subscribeFeedChannel, unsubscribeFeedChannel]);

  const {
    titleSize,
    username,
    displayName,
    isDisplayNameEnabled,
    userId,
    title,
    titleTranslationNS,
    dataElementId,
    dataTrackingPayload,
    shouldFixedTop,
    shouldShowCreatorIcon,
    shouldShowUsernameOnMobile,
    isPrimaryTextOnly,
    shouldShowInfoOnMobile,
    shouldShowMoreOnMobile,
    shouldShowTitleOnMobile,
    shouldShowLogoOnMobile,
    shouldShowBackOnMobile,
    shouldShowLoginSignUpOnMobile,
    shouldShowDiamondShopOnMobile,
    shouldShowSearchOnMobile,
    shouldShowSearchIfUnauthorizedOnMobile,
    shouldShowLeaderboardOnMobile,
    shouldShowChatroomMediaLibraryOnMobile,
    shouldHidden,
    minWidth,
    onClickMore,
    onClickInfo,
    onClickMediaLibrary,
  } = useNavHeader();

  const FeaturedList = useMemo(() => {
    const featuredList = [
      ...headerTabs
        .filter(tab => {
          if (authRequiredTabs.includes(tab)) {
            return isAuthed; // show tab if user is authed
          }
          return true;
        })
        .map(tab => TAB_CONFIG[tab] || {}),
    ];
    return (
      <FeaturedLinks>
        {featuredList.map(
          ({
            id = '',
            pathArray = [],
            linkTo = '',
            buttonId = '',
            buttonNameI18nKey = '',
          }) => {
            const isShort = NavigationId.SHORTS === id;
            if (isShort && !isShortEnabled) {
              return null;
            }
            const isMatch = pathArray.some(path =>
              matchPath(path, cleanPathname)
            );
            const path = linkTo || `/${id}`;
            const isHome = '/' === linkTo;
            return (
              <FeaturedLink
                to={path}
                key={path}
                title={t(buttonNameI18nKey, {
                  ns: TranslationNamespace.GENERAL,
                })}
                isMatch={isMatch}
                data-element_id={buttonId}
                onClick={() => {
                  if (isMatch) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    if (isHome) {
                      fetchFeeds({
                        id: renewFeedId,
                        type: feedNameWithQuery,
                        page: 1,
                        unixTimestamp: latestFeedRenewUnix,
                      });
                    }
                  }
                }}
              >
                <FeaturedLinkContent isMatch={isMatch}>
                  {t(buttonNameI18nKey, {
                    ns: TranslationNamespace.GENERAL,
                  })}
                  {id === NavigationId.LIVESTREAM && (
                    <NewBadge
                      src={NewBadgeIconSource}
                      width="24"
                      height="15"
                      alt="new badge"
                    />
                  )}
                </FeaturedLinkContent>
              </FeaturedLink>
            );
          }
        )}
      </FeaturedLinks>
    );
  }, [
    cleanPathname,
    isShortEnabled,
    isAuthed,
    t,
    fetchFeeds,
    feedNameWithQuery,
    renewFeedId,
    latestFeedRenewUnix,
    headerTabs,
  ]);

  const NavigationComponentList = useMemo(() => {
    const renderNavigationItem = ({
      key,
      buttonId,
      buttonNameI18nKey,
      path,
      isMatch,
      iconSourceUrl,
      isChat = false,
    }) => {
      const content = (
        <IconSection key={key}>
          <Tooltip
            position="bottom-center"
            renderHint={() => (
              <TooltipStyle>
                {t(buttonNameI18nKey, {
                  ns: TranslationNamespace.GENERAL,
                })}
              </TooltipStyle>
            )}
            trigger="hover"
          >
            <Icon
              as={NavigationLink}
              to={path}
              isMatch={isMatch}
              aria-label={t(buttonNameI18nKey, {
                ns: TranslationNamespace.GENERAL,
              })}
              data-element_id={buttonId}
            >
              <img
                src={iconSourceUrl}
                alt={t(buttonNameI18nKey, {
                  ns: TranslationNamespace.GENERAL,
                })}
              />
            </Icon>
          </Tooltip>
        </IconSection>
      );

      return isChat ? <ChatWrapper key={key}>{content}</ChatWrapper> : content;
    };

    return navigationList.map(
      ({
        id,
        pathArray,
        isNeedAuth,
        buttonId,
        buttonNameI18nKey,
        getPath,
        getIconSource,
      }) => {
        if (isNeedAuth && !isAuthed) {
          return null;
        }
        let basePath = '';
        let iconSourceUrl = '';
        const isMatch = pathArray.some(path => matchPath(path, cleanPathname));
        switch (id) {
          case NavigationId.CHAT: {
            basePath = getPath();
            iconSourceUrl = getIconSource({ isMatch, hasUnread });
            break;
          }
          case NavigationId.LEADERBOARD: {
            basePath = getPath({
              region: leaderboardRegions[0],
              category: leaderboardCategories[0],
            });
            iconSourceUrl = getIconSource({ isMatch, hasUnread });
            break;
          }
          default: {
            basePath = '';
            iconSourceUrl = '';
          }
        }
        const path = basePath;
        if (id === NavigationId.CHAT) {
          return renderNavigationItem({
            key: path,
            buttonId,
            buttonNameI18nKey,
            path,
            isMatch,
            iconSourceUrl,
            isChat: true,
          });
        }

        if (id === NavigationId.LEADERBOARD) {
          return renderNavigationItem({
            key: path,
            buttonId,
            buttonNameI18nKey,
            path,
            isMatch,
            iconSourceUrl,
          });
        }

        return null;
      }
    );
  }, [
    hasUnread,
    isAuthed,
    leaderboardCategories,
    leaderboardRegions,
    cleanPathname,
    t,
  ]);

  const DiamondShop = useMemo(() => {
    return (
      <IconSection>
        <Tooltip
          position="bottom-center"
          renderHint={() => (
            <TooltipStyle>
              {t('buy_points', {
                ns: TranslationNamespace.GENERAL,
              })}
            </TooltipStyle>
          )}
          trigger="hover"
        >
          <DiamondShopIcon
            data-element_id={ButtonId.All.ButtonDiamondShop}
            isMatch={isDiamondShopOpen}
            aria-label={t('buy_points', {
              ns: TranslationNamespace.GENERAL,
            })}
            onClick={addvalue}
          >
            <img
              src={ShopIconSource}
              alt={t('buy_points', {
                ns: TranslationNamespace.GENERAL,
              })}
            />
          </DiamondShopIcon>
        </Tooltip>
      </IconSection>
    );
  }, [isDiamondShopOpen, t, addvalue]);

  const More = useMemo(() => {
    return (
      <MoreIcon
        data-element_id={dataElementId}
        onClick={onClickMore}
        title={t('setting_button_more', {
          ns: TranslationNamespace.GENERAL,
        })}
      >
        <img
          src={MoreIconSource}
          alt={t('setting_button_more', {
            ns: TranslationNamespace.GENERAL,
          })}
        />
      </MoreIcon>
    );
  }, [t, onClickMore, dataElementId]);

  const ChatroomMediaLibrary = useMemo(() => {
    return (
      <MoreIcon
        data-element_id={dataElementId}
        onClick={onClickMediaLibrary}
        title={t('media_library', {
          ns: TranslationNamespace.CHAT,
        })}
      >
        <img
          src={MediaLibraryIconSource}
          alt={t('media_library', {
            ns: TranslationNamespace.CHAT,
          })}
        />
      </MoreIcon>
    );
  }, [t, onClickMediaLibrary, dataElementId]);
  const Info = useMemo(() => {
    return (
      <InfoIcon
        data-element_id={dataElementId}
        data-tracking_payload={dataTrackingPayload}
        onClick={onClickInfo}
        title={t('swagr_about_more_information', {
          ns: TranslationNamespace.GENERAL,
        })}
      >
        <img
          src={InfoIconSource}
          alt={t('swagr_about_more_information', {
            ns: TranslationNamespace.GENERAL,
          })}
        />
      </InfoIcon>
    );
  }, [t, onClickInfo, dataElementId, dataTrackingPayload]);
  const Hamburger = useMemo(() => {
    return (
      <IconSection>
        <Tooltip
          position="bottom-center"
          renderHint={() => (
            <TooltipStyle>
              {t('tabbar_menu', {
                ns: TranslationNamespace.GENERAL,
              })}
            </TooltipStyle>
          )}
          trigger="hover"
        >
          <HamburgerIcon
            data-element_id={ButtonId.Tab.TabButtonHamburger}
            isMatch={isHamburgerMenuOpen}
            aria-label={t('tabbar_menu', {
              ns: TranslationNamespace.GENERAL,
            })}
            onClick={() => {
              toggleModal({
                modalId: 'HamburgerMenuModal',
                payload: { via: 'desktop' },
                isOpen: !isHamburgerMenuOpen,
                modalOption: {
                  isHigherThanAll: true,
                },
              });
            }}
          >
            <img
              src={getNavigationIcon({
                navigationId: NavigationId.ME,
                isMatch: isHamburgerMenuOpen,
                isAuthed: false,
              })}
              alt={t('tabbar_menu', {
                ns: TranslationNamespace.GENERAL,
              })}
            />
          </HamburgerIcon>
        </Tooltip>
      </IconSection>
    );
  }, [isHamburgerMenuOpen, t, toggleModal]);
  const MeAvatarButton = useMemo(() => {
    const isMatch =
      isHamburgerMenuOpen ||
      getIsMatchedPathname({
        pathname: cleanPathname,
        dataKey: 'isOnMeProfile',
      });
    return (
      <MeAvatarSection>
        <Tooltip
          position="bottom-center"
          renderHint={() => (
            <TooltipStyle>
              {t('tabbar_menu', {
                ns: TranslationNamespace.GENERAL,
              })}
            </TooltipStyle>
          )}
          trigger="hover"
        >
          <UserButton
            data-element_id={ButtonId.Tab.TabButtonHamburger}
            aria-label={t('tabbar_menu', {
              ns: TranslationNamespace.GENERAL,
            })}
            onClick={() => {
              return toggleModal({
                modalId: 'HamburgerMenuModal',
                payload: { via: 'desktop' },
                isOpen: !isHamburgerMenuOpen,
                modalOption: {
                  isHigherThanAll: true,
                },
              });
            }}
          >
            <Avatar isMatch={isMatch}>
              <>
                <MeAvatar
                  size={AVATAR_SIZE - AVATAR_BORDER_SIZE * 2}
                  alt={meUsername}
                />
                {shouldShowPasswordBindingHint && <RedDot />}
                <MenuIconWrapper isMatch={isMatch}>
                  <img
                    src={getNavigationIcon({
                      navigationId: NavigationId.ME,
                      isMatch,
                      isAuthed: true,
                    })}
                    alt="menu icon"
                  />
                </MenuIconWrapper>
              </>
            </Avatar>
          </UserButton>
        </Tooltip>
      </MeAvatarSection>
    );
  }, [
    meUsername,
    cleanPathname,
    isHamburgerMenuOpen,
    shouldShowPasswordBindingHint,
    t,
    toggleModal,
  ]);
  const Logo = useMemo(() => {
    return (
      <LogoLink to="/" title="logo">
        <img src={logo} alt={brandname} />
      </LogoLink>
    );
  }, [brandname]);
  const Back = useMemo(() => {
    return (
      <BackIcon
        aria-label={t('back', {
          ns: TranslationNamespace.GENERAL,
        })}
        data-element_id={ButtonId.All.ButtonBack}
        onClick={goBack}
      >
        <img
          src={BackIconSource}
          alt={t('back', {
            ns: TranslationNamespace.GENERAL,
          })}
        />
      </BackIcon>
    );
  }, [t, goBack]);
  const Title = useMemo(() => {
    return (
      <HeaderTitle
        titleSize={titleSize}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        {t(title, {
          ns: titleTranslationNS,
        })}
      </HeaderTitle>
    );
  }, [t, title, titleSize, titleTranslationNS]);
  const UsernameWithIndicator = useMemo(() => {
    return (
      <>
        <UsernameWithStatus>
          <NameWrapper>
            <PrimaryTextWrapper>
              <PrimaryText
                as={Link}
                to={getUserProfileUrl({ userId, username })}
              >
                {isDisplayNameEnabled && displayName
                  ? displayName
                  : username
                    ? `@${username}`
                    : '-'}
              </PrimaryText>
              <UserLevelBadgeWrapper>
                <UserLevelBadge
                  shouldDirectToInformation={true}
                  userId={userId}
                />
              </UserLevelBadgeWrapper>
              {shouldShowCreatorIcon && <CreatorIcon src={CreatorIconSource} />}
            </PrimaryTextWrapper>
            {!isPrimaryTextOnly && (
              <SecondaryText>
                {isDisplayNameEnabled &&
                  displayName &&
                  (username ? `@${username}` : '-')}
              </SecondaryText>
            )}
          </NameWrapper>
        </UsernameWithStatus>
      </>
    );
  }, [
    userId,
    isDisplayNameEnabled,
    displayName,
    username,
    shouldShowCreatorIcon,
    isPrimaryTextOnly,
  ]);
  const SearchBar = useMemo(() => {
    return (
      <SearchBarWrapper>
        <HomeSearchBar />
      </SearchBarWrapper>
    );
  }, []);

  if (minWidth >= deviceWidth) return null;
  if (shouldHidden) return null;

  return (
    <StyledNavHeader shouldFixedTop={shouldFixedTop}>
      <LeftSide>
        <LogoWrapper $shouldShowLogoOnMobile={shouldShowLogoOnMobile}>
          {Logo}
        </LogoWrapper>
        {deviceWidth <= breakpoint.mobile && (
          <SearchWrapper
            $shouldShowSearchOnMobile={
              isAuthed ? shouldShowSearchOnMobile : undefined
            }
            $shouldShowSearchIfUnauthorizedOnMobile={
              !isAuthed ? shouldShowSearchIfUnauthorizedOnMobile : undefined
            }
          >
            {SearchBar}
          </SearchWrapper>
        )}
        <MobileOnlySection>
          <BackWrapper $shouldShowBackOnMobile={shouldShowBackOnMobile}>
            {Back}
          </BackWrapper>
        </MobileOnlySection>
        <MobileOnlySection>
          <TitleWrapper $shouldShowTitleOnMobile={shouldShowTitleOnMobile}>
            {Title}
          </TitleWrapper>
        </MobileOnlySection>
        <UsernameWrapper
          $shouldShowUsernameOnMobile={shouldShowUsernameOnMobile}
        >
          {UsernameWithIndicator}
        </UsernameWrapper>
        <DesktopOnlySection>{FeaturedList}</DesktopOnlySection>
      </LeftSide>

      <RightSide>
        <DesktopOnlySection>{SearchBar}</DesktopOnlySection>

        {isAuthed && (
          <MobileOnlySection>
            {shouldShowLeaderboardOnMobile &&
              leaderboardComponentIndex >= 0 &&
              NavigationComponentList[leaderboardComponentIndex]}
          </MobileOnlySection>
        )}
        <DesktopOnlySection>{NavigationComponentList}</DesktopOnlySection>

        {isAuthed && (
          <MobileOnlySection>
            <DiamondShopWrapper
              $shouldShowDiamondShopOnMobile={shouldShowDiamondShopOnMobile}
            >
              {DiamondShop}
            </DiamondShopWrapper>
          </MobileOnlySection>
        )}
        <DesktopOnlySection>{DiamondShop}</DesktopOnlySection>

        {isAuthed ? (
          <DesktopOnlySection>{MeAvatarButton}</DesktopOnlySection>
        ) : (
          <DesktopOnlySection>
            <LoginSignUpButton />
          </DesktopOnlySection>
        )}
        {!isAuthed && (
          <MobileOnlySection>
            {shouldShowLoginSignUpOnMobile && <LoginSignUpButton />}
          </MobileOnlySection>
        )}
        {!isAuthed && <DesktopOnlySection>{Hamburger}</DesktopOnlySection>}
        {isAuthed && (
          <MobileOnlySection>
            {shouldShowMoreOnMobile && More}
          </MobileOnlySection>
        )}
        {isAuthed && (
          <MobileOnlySection>
            {shouldShowChatroomMediaLibraryOnMobile && ChatroomMediaLibrary}
          </MobileOnlySection>
        )}
        {isAuthed && (
          <MobileOnlySection>
            {shouldShowInfoOnMobile && Info}
          </MobileOnlySection>
        )}
      </RightSide>
    </StyledNavHeader>
  );
};

NavHeader.propTypes = {
  clipCategory: PropTypes.string,
  deviceWidth: PropTypes.number,
  latestFeedRenewUnix: PropTypes.number,
  meUsername: PropTypes.string,
  feedNameWithQuery: PropTypes.string,
  renewFeedId: PropTypes.string,
  brandname: PropTypes.string,
  isAuthed: PropTypes.bool,
  hasUnread: PropTypes.bool,
  shouldShowPasswordBindingHint: PropTypes.bool,
  isHamburgerMenuOpen: PropTypes.bool,
  isDiamondShopOpen: PropTypes.bool,
  isShortEnabled: PropTypes.bool,
  leaderboardRegions: PropTypes.array,
  leaderboardCategories: PropTypes.array,
  headerTabs: PropTypes.array,
  goBack: PropTypes.func,
  toggleModal: PropTypes.func,
  addvalue: PropTypes.func,
  fetchFeeds: PropTypes.func,
  subscribeFeedChannel: PropTypes.func,
  unsubscribeFeedChannel: PropTypes.func,
};

const StyledNavHeader = styled.header`
  ${({ shouldFixedTop }) => {
    return css`
      position: ${shouldFixedTop ? 'fixed' : 'static'};
      top: ${shouldFixedTop ? 'var(--space-to-pinned-notification)' : 'auto'};
      left: ${shouldFixedTop ? '0px' : 'auto'};
    `;
  }}
  z-index: ${componentZIndex.header};
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
  padding-left: 20px;
  padding-right: 20px;
  width: 100%;
  height: ${headerHeight}px;
  display: flex;
  align-items: center;
  background-color: ${color.black};
  ${media.mobile`
    border-bottom: none;
    padding-left: 16px;
    padding-right: 16px;
  `}
`;

const HeaderTitle = styled.div`
  line-height: 1;
  font-weight: 600;
  color: ${color.white};
  font-size: ${({ titleSize }) => titleSize}px;
  cursor: pointer;
`;

const Side = styled.nav`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LeftSide = styled(Side)`
  margin-right: 12px;
  flex: auto;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
`;

const RightSide = styled(Side)`
  flex: none;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  max-width: calc(100% - 100px);
`;

const LogoWrapper = styled.div`
  display: flex;

  ${media.mobile`
    display: ${({ $shouldShowLogoOnMobile }) =>
      $shouldShowLogoOnMobile ? 'flex' : 'none'};
  `}
`;

const BackWrapper = styled.div`
  margin-right: 8px;
  display: none;

  ${media.mobile`
    display: ${({ $shouldShowBackOnMobile }) =>
      $shouldShowBackOnMobile ? 'flex' : 'none'};
  `}
`;

const TitleWrapper = styled.div`
  display: flex;

  ${media.mobile`
    display: ${({ $shouldShowTitleOnMobile }) =>
      $shouldShowTitleOnMobile ? 'flex' : 'none'};
  `}
`;

const UsernameWrapper = styled.div`
  display: none;

  ${media.mobile`
    display: ${({ $shouldShowUsernameOnMobile }) =>
      $shouldShowUsernameOnMobile ? 'flex' : 'none'};
  `}
`;

const DiamondShopWrapper = styled.div`
  display: flex;

  ${media.mobile`
    display: ${({ $shouldShowDiamondShopOnMobile }) =>
      $shouldShowDiamondShopOnMobile ? 'flex' : 'none'};
  `}
`;

const LogoLink = styled(Link)`
  margin-right: 20px;
  flex: none;
  display: flex;
  align-items: center;
  > img {
    width: auto;
    min-width: 83px;
    height: 36px;
  }
  ${media.mobile`
    margin-right: 0px;
  `}
`;

const BackIcon = styled(Icon)`
  margin-right: 8px;
  flex: none;
`;

const HamburgerIcon = styled(Icon)``;
const MoreIcon = styled(Icon)``;
const InfoIcon = styled(Icon)``;
const DiamondShopIcon = styled(Icon)``;

const NavigationLink = styled(({ isMatch, ...restProps }) => (
  <Link {...restProps} />
))``;

const MeAvatarSection = styled.div`
  margin-left: 10px;
  ${media.mobile`
    display: none;
  `}
`;

const FeaturedLinks = styled.div`
  padding-right: 14px;
  height: 100%;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  ::-webkit-scrollbar {
    display: none;
  }
  ${media.mobile`
    display: none;
  `}
`;

const FeaturedLink = styled(({ isMatch, ...restProps }) => (
  <Link {...restProps} />
))`
  position: relative;
  margin-left: 4px;
  height: 100%;
  display: inline-flex;
  align-items: center;
  :first-child {
    margin-left: 0px;
  }
  ${media.mobile`
    display: none;
  `};
  @media (hover: hover) {
    :hover {
      opacity: 1;
    }
  }
  ::after {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 8px;
    right: 8px;
    border-bottom: ${({ isMatch }) =>
      isMatch ? `solid 4px ${color.peach}` : 'none'};
  }
`;

const FeaturedLinkContent = styled.div`
  position: relative;
  border-radius: 20px;
  padding: 10px 12px;
  line-height: 24px;
  white-space: nowrap;
  font-size: 22px;
  color: ${textColor.white};
  ${media.tablet`
    padding: 8px;
    font-size: 16px;
  `}
  @media (hover: hover) {
    :hover {
      background-color: ${({ isMatch }) =>
        isMatch ? 'transparent' : 'rgba(155, 155, 155, 0.2)'};
    }
  }
`;

const NewBadge = styled.img`
  position: absolute;
  top: 0px;
  right: -6px;
  width: 24px;
  height: 15px;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  image-rendering: optimizeSpeed;
  shape-rendering: crispEdges;
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
  -webkit-font-smoothing: subpixel-antialiased;
`;

const UserButton = styled.button`
  ${ResetButtonStyle};
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  max-width: 160px;
  padding: 4px;
  border-radius: 20px;
  transition: 0.3s;
  cursor: pointer;
  ${media.tablet`
    border-radius: 50%;
  `};
`;

const SearchBarWrapper = styled.div`
  width: 100%;
  @media (max-width: ${breakpoint.tablet + 1}px) {
    display: none;
  }
  ${media.mobile`
    display: block;
  `}
`;

const ChatWrapper = styled.div`
  ${media.mobile`
    display: none;
  `}
`;

const DesktopOnlySection = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  ${media.mobile`
    display: none;
  `}
`;

const MobileOnlySection = styled.div`
  display: none;
  ${media.mobile`
    display: flex;
  `}
`;

const Avatar = styled.div`
  position: relative;
  border-radius: 50%;
  border-style: solid;
  border-width: ${AVATAR_BORDER_SIZE}px;
  border-color: ${({ isMatch }) => (isMatch ? color.peach : 'transparent')};
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  cursor: pointer;
`;

const MenuIconWrapper = styled.div.attrs(({ isMatch }) => ({
  style: {
    backgroundColor: isMatch ? color.peach : color.white,
  },
}))`
  position: absolute;
  bottom: 0px;
  right: 0px;
  border-radius: 50%;
  border: 1px solid ${color.black};
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  > img {
    width: 12px;
    height: 12px;
    object-fit: contain;
  }
`;

const RedDot = styled.div`
  position: absolute;
  top: 0px;
  right: -4px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background-color: #cf0404;
  border: solid 2px #000000;
`;

const UsernameWithStatus = styled.div`
  font-weight: 600;
  color: ${color.white};
  display: flex;
  overflow: hidden;
  ${media.mobile`
    align-items: baseline;
  `}
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PrimaryTextWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const PrimaryText = styled.div`
  color: var(--font-color-primary-on-dark, #fff);
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: 0.03px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SecondaryText = styled.div`
  color: var(--font-color-secondary-on-dark, rgba(221, 221, 221, 0.8));
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.012px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserLevelBadgeWrapper = styled.div`
  margin-left: 4px;
`;

const CreatorIcon = styled.img`
  margin-left: 4px;
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

export default memo(NavHeader);
